/* eslint-disable react-refresh/only-export-components */
/**
 * AssetsContext — 選択中素材 (scope 別) と、その追加/削除/並び替え。
 *
 * SettingsProvider の内側に置く。素材の usage 決定と動画素材の可否判定に
 * productionMode / videoGenerationMode / maxReferenceVideos (= Settings) が
 * 必要なため、依存方向は Assets → Settings の一方向。
 * Settings 側は Assets を一切参照しない (循環なし)。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useGenerationModels } from '../../../features/generation-models/hooks';
import { updateScenario } from '../../../features/scenarios/api';
import { useToast } from '../../../hooks/useToast';
import { calculateAspectRatio } from '../../materials/shared/materialUtils';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { useGenerationSettings } from './settingsContext';
import type { AudioReferenceUsage, AudioVoiceBinding, GenerationAsset, GenerationAssetUsage, VideoFrameMaterialRef, VideoGenerationMode, VideoReferenceMode } from './types';
import { inferDefaultAudioUsage, isReferenceAudioFormatAllowed } from './assetMediaUtils';
import { MOTION_LAYERED_SINGLE_MATERIAL_NOTICE, shouldRejectExtraLayerMaterial } from '../../../features/motion/layeredPromptMode';

export const STILL_IMAGE_SCOPE = '__still_image__';

/** 動画素材の実効アスペクト比 ('16:9' 等)。メタデータが無ければ width/height から算出。 */
function resolveVideoAspectRatio(asset: GenerationAsset): string | null {
  return asset.aspectRatio || calculateAspectRatio(asset.width, asset.height);
}

/** scope 内で「動画そのまま」モードの参照動画として扱われる素材の実効アスペクト比一覧。
 *  excludeId は差し替え対象 (自分自身との比較を避ける)。比率不明の素材は除外。 */
function collectReferenceVideoRatios(assets: GenerationAsset[], excludeId?: string): string[] {
  return assets
    .filter((a) => a.id !== excludeId
      && a.usage === 'reference_video'
      && (a.videoReferenceMode ?? 'video') === 'video')
    .map(resolveVideoAspectRatio)
    .filter((r): r is string => !!r);
}

export type AssetsContextValue = {
  selectedAssets: GenerationAsset[];
  // 第 2/3 引数の `opts.silent` は scenario の user_selected_asset_ids 保存トリガを
  // 抑止するためのフラグ。自動補完経路 (useProductMaterials / useFirstFrameSync /
  // handleLoadScenario の exact restore) では `silent: true` で呼び、ユーザー操作起源
  // (素材パネルからのクリック・DnD 等) では既定の `silent: false` を使う。
  addAsset: (asset: GenerationAsset, opts?: { silent?: boolean }) => void;
  addAssetToScope: (scope: string, asset: GenerationAsset, opts?: { silent?: boolean }) => void;
  /**
   * プロンプト入力モードの「モーショングラフィックス」が画面上で有効か (GenerationWorkspace の
   * isPromptMotionMode = 動画タブ + プロンプト入力 + motion + タブの表示条件)。addAsset の
   * 「レイヤー素材は 1 件」の判定に使う (motion-graphics-mode-plan.md §5.2)。レイヤー一覧の表示条件と同じ値。
   */
  setPromptMotionModeActive: (active: boolean) => void;
  /** 現在のスコープの素材を与えられた配列で丸ごと置き換える（生成履歴の復元用）。usage はそのまま保持。 */
  replaceAssets: (assets: GenerationAsset[], opts?: { silent?: boolean }) => void;
  /** 指定スコープの素材を丸ごと置き換える。saved selection の exact restore 用。 */
  replaceAssetsInScope: (scope: string, assets: GenerationAsset[], opts?: { silent?: boolean }) => void;
  removeAsset: (id: string, opts?: { silent?: boolean }) => void;
  removeAssetFromScope: (scope: string, id: string, opts?: { silent?: boolean }) => void;
  clearAssets: (opts?: { silent?: boolean }) => void;
  clearAllScopedAssets: () => void;
  /** 指定スコープ (scenarioId) の素材スナップショットを返す。activeScenarioId に依存せず、
   *  対象シナリオのスコープから直接読みたいケース (裏で完了したシナリオの自動生成等) で使う。 */
  getAssetsForScope: (scopeId: string) => GenerationAsset[];
  migrateAssetsToScopes: (newScopeIds: string[]) => void;
  updateAssetUsage: (id: string, usage: GenerationAssetUsage, opts?: { silent?: boolean }) => void;
  /** 動画素材の参考素材モード (動画そのまま / 最初 / 最後のフレーム) を切り替える。
   *  frame を渡すと抽出済みフレーム素材のキャッシュも同時に保存する。 */
  updateVideoReferenceMode: (
    id: string,
    mode: VideoReferenceMode,
    frame?: { position: 'first' | 'last' } & VideoFrameMaterialRef,
  ) => void;
  /** 参照音声の用途 (声/BGM/効果音/サウンドロゴ) と、声用途の人物バインドを更新する
   *  (docs/features/reference-audio-roles-plan.md §2.4)。voiceBinding 省略時は変更しない、
   *  null で明示的にクリア (ナレーター声) する。usage!=='voice' のときは自動でクリアする。 */
  updateAudioUsage: (
    id: string,
    usage: AudioReferenceUsage,
    voiceBinding?: AudioVoiceBinding | null,
  ) => void;
  reorderAssets: (fromIndex: number, toIndex: number, opts?: { silent?: boolean }) => void;
  activeScenarioId: string | null;
  setActiveScenarioId: (id: string | null) => void;
  /** 動画素材が選べないコンテキストで動画が選ばれたときのオファー。null=なし。
   *  受け手 (GenerationWorkspace) がコンテキスト別に処理する: 静止画タブは
   *  「動画プロンプト入力モードへ切り替えますか?」の確認モーダル、シナリオタブは
   *  トーストで選択を断る (勝手に画面遷移しない)。 */
  videoEditOffer: GenerationAsset | null;
  requestVideoEditOffer: (asset: GenerationAsset) => void;
  clearVideoEditOffer: () => void;
  /** 動画プロンプト入力モード (Gemini Omni + 参照動画) へ切り替え、指定動画を参照動画として追加する。 */
  beginVideoEdit: (asset: GenerationAsset) => void;
  /** beginVideoEdit 実行シグナル。ScenarioGenerator が表示 mode/phase をリセットする。 */
  pendingVideoEditEntry: GenerationAsset | null;
  clearVideoEditEntry: () => void;
  requestOpenPersonalFolder: () => void;
  shouldOpenPersonalFolder: boolean;
  clearOpenPersonalFolderRequest: () => void;
};

const AssetsContext = createContext<AssetsContextValue | null>(null);

export function GenerationAssetsProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const settings = useGenerationSettings();
  const {
    productionMode,
    promptVideoGenerationMode,
    videoGenerationMode,
    maxReferenceVideos,
    maxReferenceAudios,
    videoSettings,
    setProductionMode,
    setVideoSettings,
    setPromptVideoGenerationMode,
  } = settings;

  const { data: videoModels = [] } = useGenerationModels('video');

  // 素材ミューテータを安定参照 (deps 空の useCallback) にするための ref 群。
  // 旧実装ではこれらの値が value の useMemo 依存に入っていたため、設定を触るたびに
  // 全ミューテータの identity が変わり、消費者の memo 化が効かなかった。
  // 「イベントハンドラ内で最新値を読む」意味論は closure 版と同じ。
  const toastRef = useRef(toast);
  toastRef.current = toast;
  // モーショングラフィックスが画面上で有効か。値は GenerationWorkspace が setPromptMotionModeActive で入れる
  // (activeTab / scenarioMode は assetsContext から見えないため。パスで判定するとサブパス配信で外れる)
  const promptMotionModeActiveRef = useRef(false);
  const videoModeRef = useRef<{
    productionMode: typeof productionMode;
    promptVideoGenerationMode: VideoGenerationMode;
    videoGenerationMode: VideoGenerationMode;
    maxReferenceVideos: number;
    maxReferenceAudios: number;
    promptMotionModeActive: boolean;
  }>({ productionMode, promptVideoGenerationMode, videoGenerationMode, maxReferenceVideos, maxReferenceAudios, promptMotionModeActive: false });
  videoModeRef.current = {
    productionMode, promptVideoGenerationMode, videoGenerationMode, maxReferenceVideos, maxReferenceAudios,
    promptMotionModeActive: promptMotionModeActiveRef.current,
  };
  const setPromptMotionModeActive = useCallback((active: boolean) => {
    promptMotionModeActiveRef.current = active;
    videoModeRef.current = { ...videoModeRef.current, promptMotionModeActive: active };
  }, []);
  const settingsActionsRef = useRef({ setProductionMode, setVideoSettings, setPromptVideoGenerationMode });
  settingsActionsRef.current = { setProductionMode, setVideoSettings, setPromptVideoGenerationMode };
  const videoModelsRef = useRef(videoModels);
  videoModelsRef.current = videoModels;
  const videoModelCodeRef = useRef(videoSettings.videoModel);
  videoModelCodeRef.current = videoSettings.videoModel;

  // 動画編集 (v2v) 用のモデルを解決する。返り値 = 切替先の model_code / null = 切替不要。
  // - 参照動画対応モデル (Seedance 2.0/2.5 / MiniMax H3 / Gemini Omni 等) は現在の選択を維持。
  // - 参照動画非対応モデル (Veo 等) は Seedance 2.5 → Gemini Omni の順で可視なものへ。
  // モデルの可視性はパートナーごとに異なる (2.5/H3 は allow-list) ため、
  // useGenerationModels の一覧に存在するものだけを切替先候補にする。
  const resolveVideoEditModel = useCallback((): string | null => {
    const models = videoModelsRef.current;
    const currentCode = videoModelCodeRef.current || '';
    const findModel = (code: string) => models.find((m) => m.model_code === code);
    const maxVideos = (model: (typeof models)[number] | undefined) => {
      const v = (model?.metadata as { max_reference_videos?: number } | null | undefined)?.max_reference_videos;
      return typeof v === 'number' ? v : 0;
    };
    if (maxVideos(findModel(currentCode)) > 0) return null;
    if (findModel('seedance-25-standard')) return 'seedance-25-standard';
    if (findModel('gemini-omni-flash')) return 'gemini-omni-flash';
    return null;
  }, []);

  const [assetsByScope, setAssetsByScope] = useState<Record<string, GenerationAsset[]>>({});
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(() => {
    const path = window.location.pathname;
    if (path === '/studio' || path === '/studio/') {
      return localStorage.getItem('studio_active_scenario_id');
    }
    return null;
  });
  const [shouldOpenPersonalFolder, setShouldOpenPersonalFolder] = useState(false);
  // 動画素材を編集するための「プロンプト入力モードへ切替しますか?」オファー & 実行。
  const [videoEditOffer, setVideoEditOffer] = useState<GenerationAsset | null>(null);
  // beginVideoEdit の実行シグナル。ScenarioGenerator はローカルな mode/phase のみ同期する。
  const [pendingVideoEditEntry, setPendingVideoEditEntry] = useState<GenerationAsset | null>(null);

  const currentScope = activeScenarioId ?? STILL_IMAGE_SCOPE;
  const currentScopeRef = useRef(currentScope);
  currentScopeRef.current = currentScope;

  // selectedAssets の最新値を closure 経由の stale から守るための ref。
  // dirtyScopes の debounce save が走った瞬間に、setTimeout 内クロージャで参照する。
  const assetsByScopeRef = useRef(assetsByScope);
  assetsByScopeRef.current = assetsByScope;

  // assetsByScope を更新する共通ヘルパー。state 更新と同時に assetsByScopeRef.current を
  // 即時同期するため、setState のコミット (再レンダー) を待たずに getAssetsForScope が
  // 最新スコープを読める。handleLoadScenario でシナリオのスコープを復元した直後に
  // (再レンダー前に) 自動ストーリーボード生成へ素材を渡すケースで重要。
  const commitAssetsByScope = useCallback(
    (
      next:
        | Record<string, GenerationAsset[]>
        | ((prev: Record<string, GenerationAsset[]>) => Record<string, GenerationAsset[]>),
    ) => {
      const resolved =
        typeof next === 'function'
          ? (next as (p: Record<string, GenerationAsset[]>) => Record<string, GenerationAsset[]>)(
              assetsByScopeRef.current,
            )
          : next;
      assetsByScopeRef.current = resolved;
      setAssetsByScope(resolved);
    },
    [],
  );

  // ユーザー操作で selectedAssets が変わった scope の debounce 保存用タイマー。
  // scope ごとに 500ms debounce で scenarios.generation_metadata.user_selected_asset_ids へ保存する。
  // 自動補完経路は `silent: true` で markDirty を呼ばないため dirty にならない。
  //
  // 旧実装は dirtyScopes (Set state) + useEffect debounce だったが、
  // 「タイマー発火時に read → send → delete」の間にユーザー操作が走ると、
  // 同 scope の markDirty が「既に Set にある」 → no-op となり、その変更が
  // 結果的に失われる race があったため、per-scope timer を ref で管理する形に変更。
  const debounceTimersRef = useRef<Map<string, number>>(new Map());
  // 選択は画面に即時反映し (楽観的更新)、保存に失敗したら戻す。戻す先 = その保存にまとめた
  // 一連の変更の直前の状態 (scope ごと・最初の変更時に記録し、保存を送った時点で手放す)
  const rollbackBaseRef = useRef<Map<string, GenerationAsset[]>>(new Map());
  const markDirty = useCallback((scope: string, before: GenerationAsset[]) => {
    if (scope === STILL_IMAGE_SCOPE) return;  // シナリオ未紐付けは保存先がない
    const timers = debounceTimersRef.current;
    const existing = timers.get(scope);
    if (existing !== undefined) {
      window.clearTimeout(existing);
    }
    if (!rollbackBaseRef.current.has(scope)) rollbackBaseRef.current.set(scope, before);
    const timer = window.setTimeout(() => {
      timers.delete(scope);
      const base = rollbackBaseRef.current.get(scope) ?? [];
      rollbackBaseRef.current.delete(scope);
      const assets = assetsByScopeRef.current[scope] ?? [];
      const assetIds = assets.map(a => a.id).filter(Boolean);
      updateScenario(scope, { user_selected_asset_ids: assetIds })
        .catch(err => {
          console.warn('[useGenerationState] user_selected_asset_ids 保存失敗:', err);
          // 送ったあとに次の変更が入っていれば、そちらの保存に任せて戻さない
          if (assetsByScopeRef.current[scope] !== assets || timers.has(scope)) {
            toastRef.current.error('選択中の素材を保存できませんでした');
            return;
          }
          commitAssetsByScope((prev) => ({ ...prev, [scope]: base }));
          toastRef.current.error('選択中の素材を保存できなかったため、元に戻しました');
        });
    }, 500);
    timers.set(scope, timer);
  }, [commitAssetsByScope]);
  // unmount 時に未発火タイマーをクリーンアップ
  useEffect(() => {
    const timers = debounceTimersRef.current;
    return () => {
      for (const t of timers.values()) window.clearTimeout(t);
      timers.clear();
    };
  }, []);

  const selectedAssets = useMemo(() => assetsByScope[currentScope] ?? [], [assetsByScope, currentScope]);

  const addAsset = useCallback((asset: GenerationAsset, opts?: { silent?: boolean }) => {
    const isVideo = asset.contentType?.startsWith('video/');
    const isAudio = asset.contentType?.startsWith('audio/');
    // 有効な動画生成モードは production (シナリオ/プロンプト) で切り替わる。
    // 動画素材は video-to-video 対応モデル (maxReferenceVideos>0) の参照画像モードでのみ
    // 追加可能。それ以外 (フレームモード / 非対応モデル / 画像生成) では従来どおり不可。
    const modes = videoModeRef.current;
    const effectiveVideoMode = modes.productionMode === 'prompt' ? modes.promptVideoGenerationMode : modes.videoGenerationMode;
    // 参照動画 (v2v) はプロンプト入力モード限定。シーン単位生成のペイロードは
    // reference_videos を送らない (useVideoHandlers の handleGenerateSceneVideo 参照) ため、
    // シナリオ側で選ばせると生成時に無言で落ちる。合算課金モデル (Seedance 2.5 / MiniMax H3)
    // はバックエンドもシーン文脈を明示的に拒否する (references.py resolve_reference_video_urls)。
    // r2v (参照画像) は対象外 — この分岐は動画素材にのみ効く。
    const videoAllowed = effectiveVideoMode === 'reference'
      && modes.maxReferenceVideos > 0
      && modes.productionMode === 'prompt';
    // 参照音声は対応モデル (maxReferenceAudios>0 = Seedance 2.0/2.5・MiniMax H3) の
    // プロンプト入力モード限定 (backend の resolve_reference_audio_urls と同じゲート)。
    const audioAllowed = effectiveVideoMode === 'reference'
      && modes.maxReferenceAudios > 0
      && modes.productionMode === 'prompt';
    if (isAudio && !audioAllowed) {
      toastRef.current.show(
        modes.productionMode === 'prompt' && modes.maxReferenceAudios <= 0
          ? '選択中のモデルは参照音声に対応していません'
          : '参照音声はプロンプト入力モードでのみ使用できます',
      );
      return;
    }
    // 参照音声として添付可能な形式は wav/mp3 のみ (計画書 §2.6)。素材ライブラリへの
    // 保存自体は audio/aac 等も許可しているため、添付の入口 (このタイミング) で弾く。
    if (isAudio && !isReferenceAudioFormatAllowed(asset)) {
      toastRef.current.show('この形式は動画の参照音声には使用できません');
      return;
    }
    // sourceUrl 未解決 (GCS 署名付きURLの非同期取得前など) の動画・音声は、そのまま進めると
    // 空URLやサムネイルJPEGが reference_videos / reference_audios に流れて生成時に
    // 無言で無視されるため、ここで弾いて再選択を促す。
    if ((isVideo || isAudio) && !(asset.sourceUrl ?? '').trim()) {
      toastRef.current.show(`${isAudio ? '音声' : '動画'}の読み込みが完了していません。数秒待ってからもう一度選択してください`);
      return;
    }
    if (isVideo && !videoAllowed) {
      // ここでは選択せず、コンテキスト別の処理を受け手 (GenerationWorkspace) に委ねる。
      // 静止画タブ = 「動画プロンプト入力モードへ切り替えますか?」の確認モーダル、
      // シナリオタブ = トーストで断る (勝手に画面遷移させない)。
      setVideoEditOffer(asset);
      return;
    }
    const scope = currentScopeRef.current;
    // モーショングラフィックスで受け付けるレイヤー素材は 1 件だけ (motion-graphics-mode-plan.md §5.2)。
    // 静止画タブに残った productionMode='prompt' / motion で誤って弾かないよう、画面上の motion
    // (GenerationWorkspace の isPromptMotionMode) と選択済みのレイヤー素材の存在を必須条件にする
    if (shouldRejectExtraLayerMaterial({
      promptMotionModeActive: modes.promptMotionModeActive,
      current: assetsByScopeRef.current[scope] ?? [],
      asset,
    })) {
      toastRef.current.show(MOTION_LAYERED_SINGLE_MATERIAL_NOTICE);
      return;
    }
    // 参照動画の本数上限を UI でも強制する (backend は超過分を黙って切り詰めるため)。
    if (isVideo) {
      const current = assetsByScopeRef.current[scope] ?? [];
      const alreadySelected = current.some((a) => a.id === asset.id);
      const currentVideoCount = current.filter((a) => a.usage === 'reference_video').length;
      if (!alreadySelected && currentVideoCount >= modes.maxReferenceVideos) {
        toastRef.current.show(`参照動画は最大${modes.maxReferenceVideos}本までです`);
        return;
      }
      // アスペクト比の混在防止: 選択済みの参照動画 (「動画そのまま」モード) と異なる
      // 比率の動画は追加不可 (出力比率は参照動画に追従するため混在は生成矛盾になる)。
      const newRatio = resolveVideoAspectRatio(asset);
      const existingRatios = collectReferenceVideoRatios(current, asset.id);
      if (newRatio && existingRatios.some((r) => r !== newRatio)) {
        toastRef.current.show('アスペクト比が異なる動画は同時に選択できません');
        return;
      }
    }
    // 参照音声のクリップ数上限 (backend の resolve_reference_audio_urls は超過をエラーに
    // するため、UI で先に弾く)。
    if (isAudio) {
      const current = assetsByScopeRef.current[scope] ?? [];
      const alreadySelected = current.some((a) => a.id === asset.id);
      const currentAudioCount = current.filter((a) => a.usage === 'reference_audio').length;
      if (!alreadySelected && currentAudioCount >= modes.maxReferenceAudios) {
        toastRef.current.show(`参照音声は最大${modes.maxReferenceAudios}件までです`);
        return;
      }
    }

    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => {
      const prev = prevScopes[scope] ?? [];
      // 動画は (videoAllowed が参照モードを保証するので) 常に reference_video。
      // 音声も同様に (audioAllowed が保証するので) 常に reference_audio。
      // 画像は従来どおり: 参照モードは reference、フレームモードは asset.usage を尊重。
      // 判定は動画可否と同じ effectiveVideoMode (プロンプトモード時は promptVideoGenerationMode)。
      // シナリオ側モードで判定すると、シナリオ側が frame のままプロンプト参照モードを開いた
      // とき画像が first_frame になり、backend がフレーム優先で参照動画を無言で無視してしまう。
      let nextUsage: GenerationAssetUsage = isVideo
        ? 'reference_video'
        : isAudio
          ? 'reference_audio'
          : (effectiveVideoMode === 'reference' ? 'reference' : (asset.usage ?? 'reference'));

      // フレームモードで、first_frameがまだない場合は自動的にfirst_frameに設定
      if (effectiveVideoMode === 'frame' && nextUsage === 'reference') {
        const hasFirstFrame = prev.some(a => a.usage === 'first_frame');
        if (!hasFirstFrame) {
          nextUsage = 'first_frame';
        }
      }

      const existingIndex = prev.findIndex((a) => a.id === asset.id);
      // 参照音声の用途既定値推定 (計画書 §2.5 優先順位): 1) 既にこのセッションで選択済み
      // (再選択時は上書きしない) 2) asset.audioUsage (呼び出し元の明示値) 3) 素材の
      // default_audio_role / source_type からの推定。どれも無ければ undefined のまま
      // (添付時にユーザーへ選択を要求する)。
      const inferredAudioUsage = isAudio
        ? (existingIndex >= 0 ? prev[existingIndex].audioUsage : undefined)
          ?? asset.audioUsage
          ?? inferDefaultAudioUsage(asset)
        : undefined;
      if (existingIndex >= 0) {
        const copy = [...prev];
        copy[existingIndex] = {
          ...copy[existingIndex],
          ...asset,
          usage: nextUsage,
          ...(isAudio ? { audioUsage: inferredAudioUsage } : {}),
        };
        return { ...prevScopes, [scope]: copy };
      }
      return {
        ...prevScopes,
        [scope]: [...prev, { ...asset, usage: nextUsage, ...(isAudio ? { audioUsage: inferredAudioUsage } : {}) }],
      };
    });
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  // 自動経路 (保存済み選択の復元・商品素材の自動追加等) には「レイヤー素材は 1 件」の拒否を入れていない。
  // motion 中にこの経路でレイヤー素材が入ることは無い (motion は動画タブのプロンプト入力モードだけで、
  // シナリオのスコープへは入らない)。万一 2 件になっても注意文 (getMotionLayeredAssetNotice) で知らせる
  const addAssetToScope = useCallback((scope: string, asset: GenerationAsset, opts?: { silent?: boolean }) => {
    const isVideo = asset.contentType?.startsWith('video/');
    const isAudio = asset.contentType?.startsWith('audio/');
    const modes = videoModeRef.current;
    const effectiveVideoMode = modes.productionMode === 'prompt' ? modes.promptVideoGenerationMode : modes.videoGenerationMode;
    // addAsset と同じ制約 (参照動画はプロンプト入力モード限定)。
    const videoAllowed = effectiveVideoMode === 'reference'
      && modes.maxReferenceVideos > 0
      && modes.productionMode === 'prompt';
    const audioAllowed = effectiveVideoMode === 'reference'
      && modes.maxReferenceAudios > 0
      && modes.productionMode === 'prompt';
    // sourceUrl 未解決の動画・音声は追加しない (addAsset と同じ理由。自動経路のため toast は出さない)
    if ((isVideo || isAudio) && !(asset.sourceUrl ?? '').trim()) {
      return;
    }
    if (isVideo && !videoAllowed) {
      return;
    }
    if (isAudio && !audioAllowed) {
      return;
    }
    // addAsset と同じ形式制限 (計画書 §2.6)。自動経路のため toast は出さない。
    if (isAudio && !isReferenceAudioFormatAllowed(asset)) {
      return;
    }
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => {
      const prev = prevScopes[scope] ?? [];
      // 参照動画の本数上限を強制 (silent: 超過時は何もしない)
      if (isVideo) {
        const alreadySelected = prev.some((a) => a.id === asset.id);
        const currentVideoCount = prev.filter((a) => a.usage === 'reference_video').length;
        if (!alreadySelected && currentVideoCount >= modes.maxReferenceVideos) {
          return prevScopes;
        }
      }
      if (isAudio) {
        const alreadySelected = prev.some((a) => a.id === asset.id);
        const currentAudioCount = prev.filter((a) => a.usage === 'reference_audio').length;
        if (!alreadySelected && currentAudioCount >= modes.maxReferenceAudios) {
          return prevScopes;
        }
      }
      // addAsset と同じく effectiveVideoMode で判定する (シナリオ側モードとの乖離対策)
      let nextUsage: GenerationAssetUsage = isVideo
        ? 'reference_video'
        : isAudio
          ? 'reference_audio'
          : (effectiveVideoMode === 'reference' ? 'reference' : (asset.usage ?? 'reference'));
      if (effectiveVideoMode === 'frame' && nextUsage === 'reference') {
        const hasFirstFrame = prev.some(a => a.usage === 'first_frame');
        if (!hasFirstFrame) {
          nextUsage = 'first_frame';
        }
      }
      const existingIndex = prev.findIndex((a) => a.id === asset.id);
      // addAsset と同じ既定用途推定 (計画書 §2.5)。
      const inferredAudioUsage = isAudio
        ? (existingIndex >= 0 ? prev[existingIndex].audioUsage : undefined)
          ?? asset.audioUsage
          ?? inferDefaultAudioUsage(asset)
        : undefined;
      if (existingIndex >= 0) {
        const copy = [...prev];
        copy[existingIndex] = {
          ...copy[existingIndex],
          ...asset,
          usage: nextUsage,
          ...(isAudio ? { audioUsage: inferredAudioUsage } : {}),
        };
        return { ...prevScopes, [scope]: copy };
      }
      return {
        ...prevScopes,
        [scope]: [...prev, { ...asset, usage: nextUsage, ...(isAudio ? { audioUsage: inferredAudioUsage } : {}) }],
      };
    });
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const replaceAssets = useCallback((assets: GenerationAsset[], opts?: { silent?: boolean }) => {
    const scope = currentScopeRef.current;
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => ({ ...prevScopes, [scope]: assets }));
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const replaceAssetsInScope = useCallback((scope: string, assets: GenerationAsset[], opts?: { silent?: boolean }) => {
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => ({ ...prevScopes, [scope]: assets }));
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const removeAsset = useCallback((id: string, opts?: { silent?: boolean }) => {
    const scope = currentScopeRef.current;
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => ({
      ...prevScopes,
      [scope]: (prevScopes[scope] ?? []).filter((asset) => asset.id !== id),
    }));
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const removeAssetFromScope = useCallback((scope: string, id: string, opts?: { silent?: boolean }) => {
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => ({
      ...prevScopes,
      [scope]: (prevScopes[scope] ?? []).filter((asset) => asset.id !== id),
    }));
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const clearAssets = useCallback((opts?: { silent?: boolean }) => {
    const scope = currentScopeRef.current;
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => ({ ...prevScopes, [scope]: [] }));
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const clearAllScopedAssets = useCallback(() => {
    commitAssetsByScope({});
  }, [commitAssetsByScope]);

  const getAssetsForScope = useCallback((scopeId: string) => {
    // ref は毎レンダー最新の assetsByScope に同期しているため、activeScenarioId に
    // 依存せず対象スコープの最新スナップショットを返せる。
    return assetsByScopeRef.current[scopeId] ?? [];
  }, []);

  const migrateAssetsToScopes = useCallback((newScopeIds: string[]) => {
    const oldScope = currentScopeRef.current;
    const assets = assetsByScopeRef.current[oldScope] ?? [];
    if (assets.length === 0) return;
    const additions: Record<string, GenerationAsset[]> = {};
    for (const id of newScopeIds) {
      additions[id] = [...assets];
    }
    // commitAssetsByScope が ref を即時同期するため、直後 (同 tick) に
    // getAssetsForScope が新スコープを読める (自動生成へ渡せる)。
    commitAssetsByScope((prev) => ({ ...prev, ...additions }));
    // migration はシナリオ作成直後の scope コピーで、scenario create 時に既に
    // assets payload として送信済み (= backend が generation_metadata に保存済み)。
    // ここで markDirty を呼ぶと無駄な追加 PUT が走るので silent 扱い。
  }, [commitAssetsByScope]);

  // 第3引数 opts は型上のみ (usage 変更は markDirty しないため実装では未使用)。
  const updateAssetUsage = useCallback((id: string, usage: GenerationAssetUsage) => {
    const scope = currentScopeRef.current;
    commitAssetsByScope((prevScopes) => {
      const prev = prevScopes[scope] ?? [];
      const isSpecial = usage === 'first_frame' || usage === 'last_frame';
      return {
        ...prevScopes,
        [scope]: prev.map((asset) => {
          if (asset.id === id) {
            return { ...asset, usage };
          }
          if (isSpecial && asset.usage === usage) {
            return { ...asset, usage: 'reference' };
          }
          return asset;
        }),
      };
    });
    // usage 変更は user_selected_asset_ids には影響しない (id は不変) ため markDirty しない。
  }, [commitAssetsByScope]);

  const updateVideoReferenceMode = useCallback((
    id: string,
    mode: VideoReferenceMode,
    frame?: { position: 'first' | 'last' } & VideoFrameMaterialRef,
  ) => {
    const scope = currentScopeRef.current;
    // フレーム抽出モード → 「動画そのまま」への切替でもアスペクト比の混在を防止する
    // (addAsset のガードはフレームモードの動画を比較対象から除外しているため、
    //  後からの切替で混在が成立してしまう抜け道を塞ぐ)。
    if (mode === 'video') {
      const current = assetsByScopeRef.current[scope] ?? [];
      const target = current.find((a) => a.id === id);
      const newRatio = target ? resolveVideoAspectRatio(target) : null;
      const existingRatios = collectReferenceVideoRatios(current, id);
      if (newRatio && existingRatios.some((r) => r !== newRatio)) {
        toastRef.current.show('アスペクト比が異なる動画は同時に選択できません');
        return;
      }
    }
    commitAssetsByScope((prevScopes) => ({
      ...prevScopes,
      [scope]: (prevScopes[scope] ?? []).map((asset) => {
        if (asset.id !== id) return asset;
        const next: GenerationAsset = { ...asset, videoReferenceMode: mode };
        if (frame) {
          next.videoFrameMaterials = {
            ...asset.videoFrameMaterials,
            [frame.position]: { id: frame.id, url: frame.url },
          };
        }
        return next;
      }),
    }));
    // モード変更は user_selected_asset_ids に影響しない (id は不変) ため markDirty しない。
  }, [commitAssetsByScope]);

  const updateAudioUsage = useCallback((
    id: string,
    usage: AudioReferenceUsage,
    voiceBinding?: AudioVoiceBinding | null,
  ) => {
    const scope = currentScopeRef.current;
    commitAssetsByScope((prevScopes) => ({
      ...prevScopes,
      [scope]: (prevScopes[scope] ?? []).map((asset) => {
        if (asset.id !== id) return asset;
        return {
          ...asset,
          audioUsage: usage,
          // voice 以外はバインドを持たない (§2.2)。voiceBinding が明示されなければ
          // (usage 切替のみの呼び出し) 既存値を保持する。
          audioVoiceBinding: usage !== 'voice' ? null : (voiceBinding !== undefined ? voiceBinding : asset.audioVoiceBinding),
        };
      }),
    }));
    // 用途変更は user_selected_asset_ids に影響しない (id は不変) ため markDirty しない。
  }, [commitAssetsByScope]);

  const reorderAssets = useCallback((fromIndex: number, toIndex: number, opts?: { silent?: boolean }) => {
    const scope = currentScopeRef.current;
    const before = assetsByScopeRef.current[scope] ?? [];
    commitAssetsByScope((prevScopes) => {
      const prev = [...(prevScopes[scope] ?? [])];
      if (fromIndex < 0 || fromIndex >= prev.length || toIndex < 0 || toIndex >= prev.length) return prevScopes;
      const [moved] = prev.splice(fromIndex, 1);
      prev.splice(toIndex, 0, moved);
      return { ...prevScopes, [scope]: prev };
    });
    if (!opts?.silent) markDirty(scope, before);
  }, [commitAssetsByScope, markDirty]);

  const requestVideoEditOffer = useCallback((asset: GenerationAsset) => {
    setVideoEditOffer(asset);
  }, []);
  const clearVideoEditOffer = useCallback(() => {
    setVideoEditOffer(null);
  }, []);
  const clearVideoEditEntry = useCallback(() => {
    setPendingVideoEditEntry(null);
  }, []);

  const beginVideoEdit = useCallback((asset: GenerationAsset) => {
    setVideoEditOffer(null);
    // sourceUrl 未解決 (署名付きURL取得前) の動画で開始すると、reference_videos が
    // 空のまま生成されて参照動画が無言で無視されるため、ここで弾いて再試行を促す。
    if (!(asset.sourceUrl ?? '').trim()) {
      toastRef.current.show('動画の読み込みが完了していません。数秒待ってからもう一度お試しください');
      return;
    }
    const nextAsset: GenerationAsset = { ...asset, usage: 'reference_video' };

    // 遷移先が読むスコープと素材をここで同時に確定する。ScenarioGenerator の effect に
    // 格納を委ねると、未マウント時や初期化 effect との実行順に結果が依存してしまう。
    setActiveScenarioId(null);
    commitAssetsByScope((prev) => ({ ...prev, [STILL_IMAGE_SCOPE]: [nextAsset] }));

    // 参考画像 (=参照動画) モードにする。動画モデルは現在の選択を尊重し、
    // v2v 非対応 (Veo 等) のときだけ対応モデルへ自動切替する
    // (旧実装は Gemini Omni へ一律切替 + 確認モーダルだったが、Seedance 2.5 /
    //  MiniMax H3 も動画編集に対応したため 2026-08-10 に廃止)。
    const actions = settingsActionsRef.current;
    actions.setProductionMode('prompt');
    const nextModel = resolveVideoEditModel();
    if (nextModel) {
      actions.setVideoSettings((s) => ({ ...s, videoModel: nextModel as typeof s.videoModel }));
    }
    actions.setPromptVideoGenerationMode('reference');
    setPendingVideoEditEntry(nextAsset);
  }, [commitAssetsByScope, resolveVideoEditModel]);

  const requestOpenPersonalFolder = useCallback(() => {
    setShouldOpenPersonalFolder(true);
    // パネルが閉じている場合、フローティングボタンに通知ドットを表示
    useMaterialsPanelStore.getState().notifyNewMaterial();
  }, []);

  const clearOpenPersonalFolderRequest = useCallback(() => {
    setShouldOpenPersonalFolder(false);
  }, []);

  // Track previous video generation mode for mode change detection
  const previousVideoGenerationMode = useRef<VideoGenerationMode>(null);

  // Handle video generation mode changes
  useEffect(() => {
    const previousMode = previousVideoGenerationMode.current;
    const currentMode = videoGenerationMode;

    // Skip on initial mount
    if (previousMode === currentMode) {
      return;
    }

    // Update ref for next comparison
    previousVideoGenerationMode.current = currentMode;

    // フレーム利用 → 参照画像: Clear frames and update all remaining assets to reference
    // 対象スコープに素材が1つも無い場合、フレームクリア/usage変換のどちらも no-op になる
    // ため、commit ごとスキップして無意味なトーストを出さない (実際に変化が起きるケース
    // ―フレーム指定クリア・usage変換―のトーストは維持)。
    if (previousMode === 'frame' && currentMode === 'reference' && selectedAssets.length > 0) {
      const scope = currentScopeRef.current;
      const frameAssets = selectedAssets.filter(asset =>
        asset.usage === 'first_frame' || asset.usage === 'last_frame'
      );

      // reference_video / reference_audio は「参照画像」への一括変換対象にしない
      // (audio を reference にすると音声URLが reference_images に流れて生成が壊れる)。
      const toReference = (asset: GenerationAsset): GenerationAsset =>
        asset.usage === 'reference_video' || asset.usage === 'reference_audio'
          ? asset
          : { ...asset, usage: 'reference' as const };
      if (frameAssets.length > 0) {
        commitAssetsByScope(prev => ({
          ...prev,
          [scope]: (prev[scope] ?? [])
            .filter(asset => asset.usage !== 'first_frame' && asset.usage !== 'last_frame')
            .map(toReference),
        }));
        toast.show('参照画像モードに切り替わりました。フレーム指定をクリアしました。');
      } else {
        // すべての素材をreferenceに変更
        commitAssetsByScope(prev => ({
          ...prev,
          [scope]: (prev[scope] ?? []).map(toReference),
        }));
        toast.show('参照画像モードに切り替わりました。');
      }
    }

  }, [videoGenerationMode, selectedAssets, toast, commitAssetsByScope]);

  // 参照動画はプロンプト入力モード限定 (addAsset の videoAllowed と同じ規則)。
  // addAsset/addAssetToScope は新規追加をゲートするが、既存選択には効かないため、
  // productionMode 切替 (プロンプト入力 → シナリオ) や、シナリオに保存済みの
  // user_selected_asset_ids の復元で「選択済みだが使えない」reference_video 素材が
  // 残るケースをここで拾う。残したままだとシーン生成ペイロードが reference_videos を
  // 送らない経路のため無言で無視される。
  //
  // 対象はシナリオスコープのみ (activeScenarioId != null)。STILL_IMAGE_SCOPE は
  // プロンプト入力モードと共有しており、beginVideoEdit 直後 (素材を入れた直後に
  // ScenarioGenerator が mode 同期で productionMode を一瞬 'scenario' に戻しうる)
  // に消してしまう競合があるため触らない。静止画生成側は preparedAssets のメディア種別
  // フィルタで動画を除外済み (GenerationWorkspace)。
  useEffect(() => {
    if (productionMode === 'prompt') return;
    if (!activeScenarioId) return;
    const scope = currentScopeRef.current;
    const staleVideoIds = selectedAssets
      .filter((asset) => asset.usage === 'reference_video')
      .map((asset) => asset.id);
    if (staleVideoIds.length === 0) return;
    commitAssetsByScope((prev) => ({
      ...prev,
      [scope]: (prev[scope] ?? []).filter((asset) => !staleVideoIds.includes(asset.id)),
    }));
    toast.show('参照動画はプロンプト入力モードでのみ利用できるため、選択を解除しました');
  }, [productionMode, activeScenarioId, selectedAssets, toast, commitAssetsByScope]);

  // 参照音声の後追いクリーンアップ: 選択後にモデルを音声非対応 (Veo / Gemini Omni) へ
  // 切り替えた、またはプロンプト入力モードから離れたケース。残したままだと backend が
  // 無言ドロップ (非対応モデル) またはエラー (シーン生成文脈) になるため、ここで解除して
  // 気づかせる。モデル一覧ロード前 (metadata 未解決) は誤検知しないようスキップする。
  const maxReferenceAudiosForModel = useMemo(() => {
    const meta = videoModels.find((m) => m.model_code === videoSettings.videoModel)?.metadata as
      | { max_reference_audios?: number }
      | null
      | undefined;
    return typeof meta?.max_reference_audios === 'number' ? meta.max_reference_audios : 0;
  }, [videoModels, videoSettings.videoModel]);
  useEffect(() => {
    if (videoModels.length === 0) return;
    const modelResolved = videoModels.some((m) => m.model_code === videoSettings.videoModel);
    if (!modelResolved) return;
    if (maxReferenceAudiosForModel > 0 && productionMode === 'prompt') return;
    const staleAudioIds = selectedAssets
      .filter((asset) => asset.usage === 'reference_audio')
      .map((asset) => asset.id);
    if (staleAudioIds.length === 0) return;
    const scope = currentScopeRef.current;
    commitAssetsByScope((prev) => ({
      ...prev,
      [scope]: (prev[scope] ?? []).filter((asset) => !staleAudioIds.includes(asset.id)),
    }));
    toast.show(
      productionMode === 'prompt'
        ? 'このモデルは参照音声に対応していないため、選択を解除しました'
        : '参照音声はプロンプト入力モードでのみ利用できるため、選択を解除しました',
    );
  }, [videoModels, videoSettings.videoModel, maxReferenceAudiosForModel, productionMode, selectedAssets, toast, commitAssetsByScope]);

  const value = useMemo<AssetsContextValue>(() => ({
    selectedAssets,
    addAsset,
    addAssetToScope,
    setPromptMotionModeActive,
    replaceAssets,
    replaceAssetsInScope,
    removeAsset,
    removeAssetFromScope,
    clearAssets,
    clearAllScopedAssets,
    getAssetsForScope,
    migrateAssetsToScopes,
    updateAssetUsage,
    updateVideoReferenceMode,
    updateAudioUsage,
    reorderAssets,
    activeScenarioId,
    setActiveScenarioId,
    videoEditOffer,
    requestVideoEditOffer,
    clearVideoEditOffer,
    beginVideoEdit,
    pendingVideoEditEntry,
    clearVideoEditEntry,
    requestOpenPersonalFolder,
    shouldOpenPersonalFolder,
    clearOpenPersonalFolderRequest,
  }), [
    selectedAssets, addAsset, addAssetToScope, setPromptMotionModeActive, replaceAssets, replaceAssetsInScope,
    removeAsset, removeAssetFromScope, clearAssets, clearAllScopedAssets,
    getAssetsForScope, migrateAssetsToScopes, updateAssetUsage, updateVideoReferenceMode, updateAudioUsage, reorderAssets,
    activeScenarioId, videoEditOffer, requestVideoEditOffer, clearVideoEditOffer,
    beginVideoEdit, pendingVideoEditEntry, clearVideoEditEntry,
    requestOpenPersonalFolder, shouldOpenPersonalFolder, clearOpenPersonalFolderRequest,
  ]);

  return <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>;
}

/** 選択中素材だけを購読する。属性ツリー / ジョブキューの更新では再レンダーしない。 */
export function useGenerationAssets(): AssetsContextValue {
  const ctx = useContext(AssetsContext);
  if (!ctx) {
    throw new Error('useGenerationAssets must be used within GenerationProvider');
  }
  return ctx;
}

/** Provider 外では null を返す版 (合成ファサード用)。 */
export function useGenerationAssetsSafe(): AssetsContextValue | null {
  return useContext(AssetsContext);
}
